/**
 * DeleteForm Types
 */

import type { BookingCard } from "@/hooks/booking/useBookings";
import type { IServiceProject, IServiceCustomer } from "@/services";
import type { IBooking } from "@/services/types";
import { BookingType } from "@/services/types";

export type DeleteFormStep = "select" | "confirm" | "deleting";
export type DeleteFormBookingType = "hard" | "soft";

/**
 * Item representing a project or customer in the deletion selection
 */
export interface DeleteSelectionItem {
  id: number;
  name: string;
  code?: string;
  bookingCount: number;
  totalHours: number;
  isSelected: boolean;
}

/**
 * State for the DeleteForm component
 */
export interface DeleteFormState {
  step: DeleteFormStep;
  selectedProjectIds: number[];
  selectedCustomerIds: number[];
}

/**
 * Props for the DeleteForm component
 */
export interface DeleteFormProps {
  /** Selected booking cards to delete from */
  selectedCards: BookingCard[];
  /** Type of booking (hard = project, soft = customer) */
  bookingType: DeleteFormBookingType;
  /** Callback when deletion is cancelled */
  onCancel?: () => void;
  /** Callback when deletion is complete */
  onDone?: (message: string) => void;
  /** Callback when deletion fails */
  onError?: (message: string) => void;
  /** Additional CSS class */
  className?: string;
  /** Test ID for E2E testing */
  "data-testid"?: string;
}

/**
 * Result of a delete operation
 */
export interface DeleteResult {
  success: IBooking[];
  failed: IBooking[];
  forbidden: IBooking[];
}

/** Virtual project entry for absence bookings in delete selection */
const ABSENCE_DELETE_PROJECT: IServiceProject = {
  id: -1,
  code: "",
  name: "Fravær",
  is_archived: false,
};

/**
 * Extract unique projects from booking cards.
 * Absence bookings (b_type === ABSENCE) are grouped under a virtual "Fravær" entry.
 */
export function getProjectsFromCards(cards: BookingCard[]): IServiceProject[] {
  const projectMap = new Map<number, IServiceProject>();
  let hasAbsence = false;

  cards.forEach((card) => {
    card.bookings.forEach((booking) => {
      if (booking.b_type === BookingType.ABSENCE) {
        hasAbsence = true;
      } else if (booking.project && !projectMap.has(booking.project.id)) {
        projectMap.set(booking.project.id, booking.project);
      }
    });
  });

  const projects = Array.from(projectMap.values());
  if (hasAbsence) {
    projects.push(ABSENCE_DELETE_PROJECT);
  }
  return projects;
}

/**
 * Extract unique customers from booking cards
 */
export function getCustomersFromCards(
  cards: BookingCard[],
): IServiceCustomer[] {
  const customerMap = new Map<number, IServiceCustomer>();

  cards.forEach((card) => {
    card.bookings.forEach((booking) => {
      if (booking.customer && !customerMap.has(booking.customer.id)) {
        customerMap.set(booking.customer.id, booking.customer);
      }
    });
  });

  return Array.from(customerMap.values());
}

/**
 * Get all bookings from cards
 */
export function getAllBookingsFromCards(cards: BookingCard[]): IBooking[] {
  return cards.flatMap((card) => card.bookings);
}

/**
 * Get bookings for a specific project.
 * When projectId === -1, returns absence bookings.
 */
export function getBookingsByProject(
  cards: BookingCard[],
  projectId: number,
): IBooking[] {
  if (projectId === -1) {
    return getAllBookingsFromCards(cards).filter(
      (b) => b.b_type === BookingType.ABSENCE,
    );
  }
  return getAllBookingsFromCards(cards).filter(
    (b) => b.project?.id === projectId,
  );
}

/**
 * Get bookings for a specific customer
 */
export function getBookingsByCustomer(
  cards: BookingCard[],
  customerId: number,
): IBooking[] {
  return getAllBookingsFromCards(cards).filter(
    (b) => b.customer?.id === customerId,
  );
}

/**
 * Calculate total hours for bookings
 */
export function calculateTotalHours(bookings: IBooking[]): number {
  return bookings.reduce((sum, b) => sum + b.hours, 0);
}
